import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useShelterState } from '../../hooks/useShelter';
import { TranslationKeyEnum } from '../../types/constants';
import { OptInRateInsights } from '../../types/insights';
import { Card } from '../Card/Card';
import { MonthlyBarChart } from '../charts/MonthlyBarChart';
import { Tooltip } from '../Tooltip';
import { trackEvent } from '../../services/analytics';
import { usePetParentReportActions } from '../../hooks/usePetParentReports';

interface OptInRateCardProps {
  data: OptInRateInsights;
  className?: string;
}

export const OptInRateCard: React.FC<OptInRateCardProps> = ({ data, className = '' }) => {
  const { t } = useTranslation();
  const { downloadOptinGuide } = usePetParentReportActions();
  const { shelter } = useShelterState();

  // Prepare chart data
  const chartData = data.monthlyData.map((point) => ({
    month: point.month,
    year: point.year,
    optIns: point.optIns,
    totalAdoptions: point.totalAdoptions,
    optInRate: point.optInRate,
    isPastMonth: point.isPastMonth,
    hasZeroOptIns: point.hasZeroOptIns,
    reportMissing: point.reportMissing,
    isFirstReportDueMonth: point.isFirstReportDueMonth,
    isZeroZeroState: point.isZeroZeroState,
    isMissedMonth: point.isMissedMonth,
    isCurrentMonth: point.isCurrentMonth,
  }));

  const chartSeries = [
    {
      key: 'optIns',
      color: '#CDDEEE',
      label: t(TranslationKeyEnum.InsightsTotalOptIns),
    },
    {
      key: 'totalAdoptions',
      color: '#6396C9',
      label: t(TranslationKeyEnum.TotalAdoptions),
    },
  ];

  const Legend: React.FC = () => (
    <>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[0].color }}
        />
        <span className="text-base font-medium text-black/60">
          {t(TranslationKeyEnum.InsightsTotalOptIns)}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[1].color }}
        />
        <span className="text-base font-medium text-black/60">
          {t(TranslationKeyEnum.TotalAdoptions)}
        </span>
      </div>
    </>
  );

  return (
    <Card
      background="white"
      radius="large"
      padding="none"
      className={`border border-black/20 overflow-visible ${className}`}
    >
      <div className="flex flex-col gap-12 p-12">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-3xl font-bold text-black/90 tracking-tight">
            {t(TranslationKeyEnum.PetParentOptInRate)}
          </h3>

          {/* Legend (hide on mobile) */}
          <div className="hidden lg:flex items-center gap-6">
            <Legend />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-18">
          {/* Left Side - Status and Metrics */}
          <div className="flex flex-wrap sm:flex-nowrap gap-8 justify-between flex-shrink-0 w-full lg:w-fit">
            {/* Percentage Section */}
            <div className="flex flex-col gap-8 w-full">
              <div className="flex flex-col gap-3">
                <div className="text-8xl font-bold text-black/90 leading-none">
                  {data.overallOptInRate !== null ? `${data.overallOptInRate}%` : '--'}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-base font-semibold text-black/90">
                    {t(TranslationKeyEnum.OptedInToHillsCommunications)}
                  </span>
                  <Tooltip
                    align="right"
                    content={
                      <div className="w-[360px]  p-4">
                        <div className="space-y-2">
                          <div className="text-sm font-bold text-white leading-[1.375]">
                            {t(TranslationKeyEnum.WhatIsOptInRate)}
                          </div>
                          <div className="text-sm font-extralight text-white leading-[1.375]">
                            {t(TranslationKeyEnum.OptInRateTooltipInfoLine1)}
                          </div>
                          <div className="text-sm font-normal text-white leading-[1.375]">
                            {t(TranslationKeyEnum.PetParentReportsTooltipDescription1)}{' '}
                            <button
                              className="underline bg-transparent p-0 m-0"
                              onClick={() => {
                                trackEvent({
                                  action: 'opt-in_guide',
                                  category: 'pet_parent_report',
                                  label: t(TranslationKeyEnum.HillsOptInGuide) ?? '',
                                });
                                downloadOptinGuide();
                              }}
                            >
                              {t(TranslationKeyEnum.HillsOptInGuide)}
                            </button>{' '}
                            {t(TranslationKeyEnum.PetParentReportsTooltipDescription2)}
                          </div>
                          {shelter && !shelter.is_petszel_enabled && (
                            <>
                              <hr className="mt-2 mb-2 opacity-20" />
                              <div className="text-sm font-extralight text-white leading-[1.375]">
                                {t(TranslationKeyEnum.OptInRateTooltipInfoLine3)}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>

              <div className="w-full h-px bg-black/20" />

              <div className="flex items-center gap-1.5">
                <span className="text-base font-medium text-black/60">
                  {t(TranslationKeyEnum.OptInRateTarget, data.programTarget)}
                </span>
              </div>
            </div>
            {/* show below legend on mobile */}
            <div className="flex flex-col lg:hidden items-start gap-2 flex-shrink-0">
              <Legend />
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <MonthlyBarChart
              type={'opt-in'}
              data={chartData}
              series={chartSeries}
              height={314}
              hasOptInData={data.hasOptInData}
              hasAdoptionData={data.hasAdoptionData}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OptInRateCard;
